import React, { useState } from "react"
import {
  Box,
  Typography,
  Button,
  TextField,
  MenuItem,
  InputAdornment,
  Chip,
  Grid,
  Paper,
} from "@mui/material"
import AddIcon from "@mui/icons-material/Add"
import SearchIcon from "@mui/icons-material/Search"
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined"
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth"
import BusinessIcon from "@mui/icons-material/Business"
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline"
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline"
import SectionBox from "./SectionBox"

export default function ProjectListTab() {
  const [status, setStatus] = useState("all")
  const [contractor, setContractor] = useState("all")

  const handleStatusChange = (e) => setStatus(e.target.value)
  const handleContractorChange = (e) => setContractor(e.target.value)
  const handleClearFilters = () => {
    setStatus("all")
    setContractor("all")
  }

  // shared style for all filter fields
  const filterFieldStyle = {
    backgroundColor: "#F9FAFB",
    borderRadius: "8px",
    minWidth: "220px",
    flex: 1,
    "& .MuiOutlinedInput-root": {
      "& fieldset": { borderColor: "#E4E7EC" },
      "&:hover fieldset": { borderColor: "#D0D5DD" },
      "&.Mui-focused fieldset": { borderColor: "#6A00FF" },
      height: "40px",
    },
    "& .MuiInputBase-input": {
      fontSize: "14px",
      color: "#101828",
    },
  }

  // 🔹 Mock projects
  const projects = [
    {
      id: "PRJ-001",
      name: "Downtown Office Complex",
      status: "Ongoing",
      contractors: ["ABC Construction", "XYZ Electric"],
      duration: "2024-01-15 - 2024-06-30",
      violations: 2,
    },
    {
      id: "PRJ-002",
      name: "Residential Tower Phase 1",
      status: "Completed",
      contractors: ["BuildCorp"],
      duration: "2023-08-01 - 2024-02-28",
      violations: 0,
    },
    {
      id: "PRJ-003",
      name: "Shopping Mall Renovation",
      status: "Ongoing",
      contractors: ["ABC Construction", "Modern Plumbing"],
      duration: "2024-03-01 - 2024-09-15",
      violations: 5,
    },
  ]

  return (
    <Box sx={{ mt: "3vh" }}>
      {/* Header row */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: "3vh",
        }}
      >
        <Box>
          <Typography
            variant="h6"
            sx={{ fontSize: "16px", fontWeight: 500, color: "#0D0D0E" }}
          >
            Project List
          </Typography>
          <Typography variant="body2" sx={{ color: "#667085" }}>
            Manage and monitor all workforce projects
          </Typography>
        </Box>

        <Button
          variant="contained"
          startIcon={<AddIcon />}
          sx={{
            backgroundColor: "#6A00FF",
            textTransform: "none",
            fontSize: "14px",
            fontWeight: 500,
            borderRadius: "8px",
            height: "38px",
            "&:hover": { backgroundColor: "#5a00d6" },
          }}
        >
          Create Project
        </Button>
      </Box>

      {/* Filters Box */}
      <SectionBox
        icon={<FilterAltOutlinedIcon sx={{ fontSize: "20px", color: "#101828" }} />}
        title="Filters">

                  {/* Filter Inputs */}
                  <Box
                      sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: "1rem",
                          flexWrap: "wrap",
                      }}
                  >
                      {/* Search */}
                      <TextField
                          placeholder="Search by name or ID..."
                          size="small"
                          sx={filterFieldStyle}
                          InputProps={{
                              startAdornment: (
                                  <InputAdornment position="start">
                                      <SearchIcon sx={{ color: "#667085", fontSize: "20px" }} />
                                  </InputAdornment>
                              ),
                          }}
                      />

                      {/* Status */}
                      <TextField
                          select
                          size="small"
                          value={status}
                          onChange={handleStatusChange}
                          sx={filterFieldStyle}
                      >
                          <MenuItem value="all">All</MenuItem>
                          <MenuItem value="active">Active</MenuItem>
                          <MenuItem value="completed">Completed</MenuItem>
                          <MenuItem value="pending">Pending</MenuItem>
                      </TextField>

                      {/* Contractor */}
                      <TextField
                          select
                          size="small"
                          value={contractor}
                          onChange={handleContractorChange}
                          sx={filterFieldStyle}
                      >
                          <MenuItem value="all">All Contractors</MenuItem>
                          <MenuItem value="a">Contractor A</MenuItem>
                          <MenuItem value="b">Contractor B</MenuItem>
                      </TextField>

                      {/* Clear Filters (styled same as inputs) */}
                      <Button
                          onClick={handleClearFilters}
                          variant="outlined"
                          disableElevation
                          sx={{
                              ...filterFieldStyle,
                              textTransform: "none",
                              justifyContent: "center",
                              borderColor: "#E4E7EC",
                              border: "1px solid #E4E7EC",
                              borderRadius: "0px",
                              color: "#344054",
                              "&:hover": {
                                  backgroundColor: "#F2F4F7",
                                  borderColor: "#D0D5DD",
                              },
                          }}
                      >
                          Clear Filters
                      </Button>
                  </Box>
        </SectionBox>


       {/* Project Cards Grid */}
        <Box sx={{ width: "100%", overflow: "hidden", pt: 2 }}>
            <Grid
                container
                spacing={0}
                sx={{
                margin: 0,
                display: "flex",
                flexWrap: "wrap",
                gap: "24px",
                justifyContent: "flex-start",
                }}
            >
                {projects.map((project) => (
                <Grid
                    item
                    key={project.id}
                    sx={{
                    flex: "1 1 calc((100% - 48px) / 3)", // ✅ exactly 3 per row with 24px gaps
                    display: "flex",
                    minWidth: "300px", // prevents squishing on smaller screens
                    "@media (max-width:1200px)": {
                        flex: "1 1 calc((100% - 24px) / 2)", // 2 per row on tablet
                    },
                    "@media (max-width:700px)": {
                        flex: "1 1 100%", // 1 per row on mobile
                    },
                    }}
                >
                    <Paper
                    elevation={0}
                    sx={{
                        border: "1px solid #E4E7EC",
                        borderRadius: "10px",
                        padding: "1.5rem",
                        width: "100%",
                        height: "100%",
                        boxSizing: "border-box",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "space-between",
                    }}
                    >
                    {/* Title + Status */}
                    <Box
                        sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        }}
                    >
                        <Typography
                        sx={{
                            fontWeight: 600,
                            color: "#0D0D0E",
                            fontSize: "16px",
                        }}
                        >
                        {project.name}
                        </Typography>

                        <Chip
                        label={project.status}
                        sx={{
                            height: "22px",
                            fontSize: "12px",
                            fontWeight: 500,
                            color:
                            project.status === "Completed" ? "#027A48" : "#6941C6",
                            backgroundColor:
                            project.status === "Completed"
                                ? "rgba(3, 152, 85, 0.15)"
                                : "rgba(105, 65, 198, 0.1)",
                        }}
                        />
                    </Box>

                    <Typography
                        sx={{ color: "#667085", fontSize: "14px", mt: 0.5, mb: 1.5 }}
                    >
                        ID: {project.id}
                    </Typography>

                    {/* Contractors */}
                    <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                        <BusinessIcon sx={{ fontSize: "18px", color: "#667085", mr: 1 }} />
                        <Typography sx={{ color: "#667085", fontSize: "14px", mr: 1 }}>
                        Contractors:
                        </Typography>
                        <Box sx={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
                        {project.contractors.map((c, i) => (
                            <Chip
                            key={i}
                            label={c}
                            size="small"
                            sx={{
                                fontSize: "12px",
                                borderRadius: "16px",
                                backgroundColor: "#F9FAFB",
                                border: "1px solid #E4E7EC",
                            }}
                            />
                        ))}
                        </Box>
                    </Box>

                    {/* Duration */}
                    <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
                        <CalendarMonthIcon
                        sx={{ fontSize: "18px", color: "#667085", mr: 1 }}
                        />
                        <Typography sx={{ color: "#667085", fontSize: "14px" }}>
                        Duration: {project.duration}
                        </Typography>
                    </Box>

                    <Box sx={{ borderBottom: "1px solid #E4E7EC", mb: 1.5 }} />

                    {/* Violations */}
                    {project.violations > 0 ? (
                        <Box sx={{ display: "flex", alignItems: "center", color: "#D92D20" }}>
                        <ErrorOutlineIcon sx={{ fontSize: "18px", mr: 0.5 }} />
                        <Typography sx={{ fontSize: "14px", fontWeight: 500 }}>
                            {project.violations} violations
                        </Typography>
                        </Box>
                    ) : (
                        <Box sx={{ display: "flex", alignItems: "center", color: "#027A48" }}>
                        <CheckCircleOutlineIcon sx={{ fontSize: "18px", mr: 0.5 }} />
                        <Typography sx={{ fontSize: "14px", fontWeight: 500 }}>
                            No violations
                        </Typography>
                        </Box>
                    )}
                    </Paper>
                </Grid>
                ))}
            </Grid>
        </Box>
    </Box>
  )
}
